import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '../Box/Box';
import { Tabs, TabStyled } from './Tab.styled';

type TabsWithRoutingProps = {
  onTabChange?: (newValue: string) => void;
  defaultSelectedIndex?: number;
  tabs: TabConfig[];
};

type TabDetails = {
  index: number;
  label: string;
  pathName: string;
  isHidden: boolean;
};

type TabConfig = TabDetails & {
  renderTab: (props: TabDetails) => JSX.Element;
};

const TabsWithRouting = ({
  onTabChange = () => {},
  defaultSelectedIndex = 0,
  tabs,
  ...otherProps
}: TabsWithRoutingProps) => {
  const nav = useLocation();
  const defaultTab = tabs.find(({ index }) => index === defaultSelectedIndex);
  const { '*': activeTabPathName } = useParams();
  const [selectedTabPath, setSelectedTabPath] = useState<string | undefined>(activeTabPathName || tabs[0].pathName);

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTabPathName === '') {
      const redirectTo = nav.pathname + '/' + defaultTab?.pathName;
      navigate(redirectTo, { replace: true });
    }
  }, [activeTabPathName]);

  const getBasePath = (fullPath: string, parameter?: string) => {
    if (!parameter || parameter === '') return fullPath;

    const fullPathArray = fullPath.split('/');
    const parameterArray = parameter.split('/');

    return fullPathArray.slice(0, -parameterArray.length).join('/');
  };
  const base = getBasePath(nav.pathname, activeTabPathName);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTabPath(() => {
      onTabChange(newValue);
      return newValue;
    });
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        TabIndicatorProps={{ sx: { height: '3px' } }}
        value={selectedTabPath || false}
        onChange={handleChange}
        sx={{ pb: 0.5 }}
        {...otherProps}
      >
        {tabs
          .filter(({ isHidden }) => !isHidden)
          .map(({ label, pathName }) => {
            return (
              <TabStyled
                key={`tab-styled-${pathName}`}
                label={label}
                value={pathName}
                to={`${base}/${pathName}`}
                component={NavLink}
              />
            );
          })}
      </Tabs>
      <Box className="TabPanel-container" sx={{ flex: 2, display: 'flex' }}>
        {tabs
          .filter(({ isHidden }) => !isHidden)
          .map(({ index, renderTab, label, isHidden, pathName }) => {
            if (pathName !== selectedTabPath) return null;

            return (
              <Box
                className="TabPanel-root"
                sx={{ p: 1, height: '100%', width: '100%' }}
                role="tabpanel"
                hidden={pathName !== selectedTabPath}
                id={`generic-tabpanel-${index}`}
                aria-labelledby={`generic-tab-${index}`}
                key={`tab-panel-${pathName}`}
              >
                {renderTab({ index, label, pathName, isHidden })}
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default TabsWithRouting;
